import { useDemoMode } from "@/contexts/DemoModeContext";
import React from "react";

interface Tab {
  id: string;
  label: string | React.ReactNode;
  disabled?: boolean;
  grayStyle?: boolean;
  icon?: string;
  iconActive?: string;
  badge?: string | number;
  customActiveColor?: string;
  customActiveBorderColor?: string;
  demoOnly?: boolean;
}

interface ModuleTabsProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
}

export function ModuleTabs({ tabs, activeTab, onTabChange }: ModuleTabsProps) {
  const { isDemoMode } = useDemoMode();
  const isTabDisabled = (tab: Tab) => {
    return (tab?.demoOnly && !Boolean(isDemoMode)) || tab?.disabled;
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={(e) => {
            if (isTabDisabled(tab)) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            onTabChange(tab.id);
          }}
          disabled={isTabDisabled(tab)}
          onKeyDown={(e) => {
            if (isTabDisabled(tab) && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          aria-disabled={isTabDisabled(tab) || undefined}
          tabIndex={isTabDisabled(tab) ? -1 : 0}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 relative ${
            isTabDisabled(tab)
              ? "bg-gray-300 text-gray-500 cursor-not-allowed border border-gray-300 pointer-events-none"
              : tab.grayStyle
              ? "bg-gray-300 text-gray-500 hover:bg-gray-400 border border-gray-400 shadow-sm"
              : activeTab === tab.id
              ? `${
                  tab.customActiveColor || "bg-blue-500"
                } text-white shadow-md border ${
                  tab.customActiveBorderColor || "border-blue-600"
                }`
              : "bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-700 border border-gray-300 shadow-sm hover:shadow-md hover:border-blue-300 cursor-pointer"
          }`}
        >
          {(tab.icon || tab.iconActive) && (
            <img
              src={
                activeTab === tab.id && tab.iconActive
                  ? tab.iconActive
                  : tab.icon || tab.iconActive
              }
              alt=""
              className="w-6 h-6 object-contain"
            />
          )}
          {tab.label}
          {tab.badge && (
            <span className="absolute -top-2 -right-7 bg-red-600 text-white text-xs font-semibold rounded-full h-[20px] flex items-center justify-center px-2.5 shadow-md whitespace-nowrap">
              +{tab.badge} сегодня
            </span>
          )}
          {isTabDisabled(tab)}
        </button>
      ))}
    </div>
  );
}
